export const analysisFr = {
  title: "Profils d'analyse et nutritionnels",
  subtitle:
    'Valeurs de laboratoire et calculées versionnées, avec traçabilité, priorité contrôlée et approbation.',
  help: { open: "Ouvrir l'aide sur les analyses, les calculs et les approbations" },
  pagination: 'Page {{page}} sur {{pages}} · {{total}} versions',
  actions: {
    parameters: "Paramètres d'analyse",
    newParameter: 'Nouveau paramètre',
    newLaboratory: 'Saisir un profil laboratoire',
    calculate: 'Calculer la recette',
    addValue: 'Ajouter une valeur',
    submit: 'Soumettre au contrôle',
    review: 'Contrôle technique',
    approve: 'Approuver',
    copy: 'Nouvelle version',
    archive: 'Archiver',
    history: 'Historique des versions',
  },
  filters: {
    searchProfiles: 'Rechercher numéro, article ou recette',
    searchParameters: 'Rechercher clé ou désignation',
    domain: 'Domaine, p. ex. NUTRITION',
    allStatuses: 'Tous les statuts',
    allOrigins: 'Toutes les origines',
  },
  statuses: {
    DRAFT: 'Brouillon',
    IN_REVIEW: 'En contrôle',
    APPROVED: 'Approuvé',
    SUPERSEDED: 'Remplacé',
    ARCHIVED: 'Archivé',
  },
  origins: { ANALYZED: 'Analysé', CALCULATED: 'Calculé', MIXED: 'Valeur laboratoire prioritaire' },
  targetTypes: { PRODUCT: 'Article', RECIPE: 'Recette' },
  issueSeverity: { ERROR: 'Erreur', WARNING: 'Avertissement' },
  aggregationModes: {
    MASS_BALANCE: 'Bilan massique / quantité additive',
    WEIGHTED_AVERAGE: 'Moyenne pondérée',
    NOT_CALCULABLE: 'Non calculable automatiquement',
  },
  roundingModes: { HALF_UP: 'Arrondi usuel', DOWN: 'Tronquer', UP: "S'éloigner de zéro" },
  columns: {
    number: 'Numéro',
    target: 'Article / recette',
    domain: 'Domaine',
    origin: 'Origine',
    reference: 'Quantité de référence',
    version: 'Version',
    status: 'Statut',
    issues: 'Erreurs / avertissements',
    key: 'Clé',
    name: 'Désignation',
    parameter: 'Paramètre',
    effective: 'Valeur approuvée',
    calculated: 'Calculé',
    analyzed: 'Analysé',
    range: 'Min / cible / max / tolérance',
    difference: 'Écart',
    hash: 'Empreinte du snapshot',
    created: 'Créé',
    validity: 'Validité',
  },
  fields: {
    key: 'Clé stable',
    domain: 'Domaine configurable',
    defaultUnit: 'Unité de valeur',
    reference: 'Quantité et unité de référence',
    referenceQuantity: 'Quantité de référence',
    referenceUnit: 'Unité de référence',
    aggregation: 'Mode de calcul',
    retention: 'Facteur de rétention',
    rounding: 'Décimales / arrondi',
    metadata: 'Métadonnées configurables (JSON)',
    changeReason: 'Motif de modification',
    validFrom: 'Valide à partir du',
    validTo: "Valide jusqu'au",
    name: 'Désignation',
    shortName: 'Nom court',
    description: 'Description',
    reason: 'Motif / note de contrôle',
    number: 'Numéro de profil',
    numberFirst: 'Numéro de profil (première version)',
    targetType: 'Type de cible',
    target: 'Cible',
    source: 'Source / laboratoire / rapport',
    method: 'Méthode de mesure',
    sampledAt: 'Date de prélèvement / mesure',
    recipe: 'Version de recette approuvée',
    laboratoryOverride: 'Version laboratoire approuvée avec priorité contrôlée',
    overrideReason: 'Motif de priorité du laboratoire',
    parameter: "Paramètre d'analyse",
    value: 'Valeur',
    unitCode: 'Unité',
    minValue: 'Minimum',
    maxValue: 'Maximum',
    targetValue: 'Cible',
    toleranceValue: 'Tolérance',
  },
  parameters: {
    title: "Paramètres d'analyse configurables",
    subtitle:
      'Paramètres génériques; les valeurs nutritionnelles sont définies uniquement par des domaines configurables.',
    createTitle: "Créer un paramètre d'analyse",
    editTitle: "Modifier le paramètre d'analyse v{{version}}",
    editorHelp:
      'Toutes les langues sont requises avant contrôle. Unités, rétention et arrondi pilotent les calculs serveur reproductibles.',
    historyTitle: "Historique du paramètre d'analyse",
  },
  calculate: {
    title: "Calculer les valeurs d'analyse d'une recette",
    description:
      'Résout les sous-recettes et tient compte des quantités, pertes, rendement, absorption, rétention et arrondis.',
    noOverride: 'Utiliser uniquement les valeurs calculées',
    reviewWarning:
      'Le résultat est un brouillon à contrôler. Une source absente ou périmée ne sera jamais remplacée silencieusement.',
  },
  laboratory: {
    title: 'Saisir un profil analysé',
    description:
      'Toute valeur manuelle exige une source traçable. Les rapports sont ensuite déposés uniquement dans le dossier GED protégé.',
    values: "Valeurs de mesure et d'analyse",
    valueHelp:
      'Minimum, maximum, cible et tolérance sont facultatifs; valeur et unité sont obligatoires.',
    dmsHelp:
      'Après création, téléverser les rapports dans le profil, les sélectionner dans le brouillon et figer leurs empreintes.',
    editTitle: 'Modifier le brouillon analysé',
    editDescription:
      'Les valeurs et la sélection GED utilisent un verrouillage optimiste. Le contenu devient immuable après soumission.',
  },
  review: {
    title: 'Contrôle technique requis',
    description:
      'Les valeurs calculées ou de laboratoire prioritaires ne constituent pas une approbation technique ou juridique automatique. Sources, alertes et écarts doivent être contrôlés.',
  },
  sections: {
    values: 'Valeurs approuvées, calculées et de laboratoire',
    valuesHelp:
      'La valeur approuvée est celle du snapshot effectif. Pour MIXED, le calcul reste visible à titre comparatif.',
  },
  summary: {
    reference: 'Référence',
    values: 'Paramètres',
    errors: 'Erreurs',
    warnings: 'Avertissements',
    validity: 'Validité',
  },
  tabs: {
    values: 'Valeurs',
    sources: 'Traçabilité',
    validation: 'Validation',
    comparison: 'Comparaison',
    documents: 'Rapports',
    history: 'Versions',
    snapshot: 'Snapshot',
  },
  sources: {
    manual: 'Valeur saisie manuellement ou analysée en laboratoire',
    noCalculated:
      "Aucune contribution matière calculée automatiquement n'existe pour cette valeur.",
  },
  comparison: {
    title: 'Comparer les versions et les valeurs laboratoire/calculées',
    description:
      'Les valeurs sont normalisées dans des unités compatibles et les écarts décimaux exacts sont affichés.',
  },
  documents: {
    title: "Rapports de laboratoire et d'essai protégés",
    snapshotSelection: 'Figer les documents de ce brouillon',
    snapshotHelp:
      'Seuls les fichiers sélectionnés et leurs empreintes SHA-256 font partie de cette version.',
  },
  history: {
    title: 'Historique de versions immuable',
    description:
      'Les versions approuvées restent reproductibles malgré les modifications ultérieures des sources.',
  },
  snapshot: { sources: 'Snapshot des sources', calculation: 'Snapshot du calcul' },
  states: {
    loading: "Chargement des valeurs d'analyse…",
    noProfiles: "Aucune version de profil d'analyse ne correspond aux filtres.",
    noParameters: "Aucune version de paramètre d'analyse ne correspond aux filtres.",
    noIssues: 'Aucune erreur ni avertissement de validation.',
    notFound: "La version du profil d'analyse est introuvable.",
  },
  messages: {
    saved: "Brouillon d'analyse enregistré.",
    created: 'Profil laboratoire créé.',
    calculated: 'Brouillon de calcul reproductible créé.',
    workflow: "Workflow d'analyse mis à jour.",
  },
  workflow: {
    REVIEW: {
      title: 'Documenter le contrôle technique',
      description: "Le contrôleur doit être indépendant de l'auteur et du soumissionnaire.",
    },
    APPROVE: {
      title: "Approuver le profil d'analyse",
      description:
        'L’approbation fige valeurs, sources, règles, empreintes documentaires et avertissements.',
    },
    COPY: {
      title: 'Copier la version analysée',
      description: 'Crée un nouveau brouillon modifiable avec traçabilité complète.',
    },
    ARCHIVE: {
      title: "Archiver la version d'analyse",
      description: "La version probante reste lisible mais n'est plus active.",
    },
  },
} as const;
